<template>
    <ReoDialog
        :title="hasSimInfo?$t('subscribe_plan.input_sim.select_sim'): undefined"
        :labelOfConfirm="hasSimInfo?$t('common.next'):undefined"
        :isLargeSize="true"
        :disableConfirm="!hasSelectedSim"
        :showCloseIcon="hasSimInfo || !isMobile"
        :showButton="hasSimInfo"
        @confirm="handleNext"
        @cancel="$emit('change',false)"
        v-if="showSelectSim"
    >
        <div class="select-sim-dialog">
            <SimInfoCard
                v-for="simInfo in simInfoList"
                :key="simInfo.data.simCard?.iccid"
                select
                sim-select-mode
                :simManager="simInfo"
                :identifier="infiniteIdentifier"
                :isChecked="simInfo.selected"
                @change="(val)=>changeSim(simInfo, val)"
            />
            <infiniteLoading
                @infinite="infiniteHandler"
                spinner="spiral"
                :identifier="infiniteIdentifier"
                class="loading"
            >
                <div slot="spinner">
                    <div class="processing-container">
                        <processing/>
                    </div>
                </div>
                <div slot="no-more"><no-more/></div>
                <div slot="no-results"><no-sim/></div>
                <div slot="error"><no-sim/></div>
            </infiniteLoading>
        </div>
    </ReoDialog>
</template>

<script lang="ts">
import Vue from 'vue';
import ReoDialog from '@/basic/Dialog.vue';
import NoSim from '@/components/NoSim.vue';
import { SimManager } from '@/lib/simManager';
import { mapGetters } from 'vuex';
import SimInfoCard from '@/components/SimInfoCard.vue';
import { getSimCardList } from '@/api/sim';
import { ESimCardModule } from '@reolink-cloud/interface';
import { productsManager } from '@/lib';
import nprogress from 'nprogress';
import { unfoldError } from '@/common/js/notice';
import processing from '@/basic/processing.vue';
import InfiniteLoading, { StateChanger } from 'vue-infinite-loading';
import NoMore from '@/view/library/NoMore.vue';

export default Vue.extend({
    components: {
        ReoDialog,
        NoSim,
        SimInfoCard,
        InfiniteLoading,
        processing,
        NoMore
    },
    model: {
        prop: 'showSelectSim',
        event: 'change'
    },
    props: {
        showSelectSim: Boolean
    },
    data() {

        return {
            simInfoList: [] as SimManager[],
            infiniteIdentifier: 1,
            pageInfo: {
                page: 1,
                limit: 30,
                total: 1,
            },
        };

    },
    computed: {
        ...mapGetters<'isMobile'|'lang'>( ['isMobile', 'lang'] ),
        hasSimInfo(): boolean {

            return this.simInfoList.length > 0;

        },
        hasSelectedSim(): boolean {

            return this.simInfoList.some( item => item.selected );

        }
    },
    watch:{
        showSelectSim( newVal: boolean ) {

            if ( newVal ) {

                this.simInfoList = [];
                this.pageInfo.page = 1;
                this.pageInfo.total = 1;
                this.infiniteIdentifier++;

            }

        }
    },
    async created() {

        await productsManager.update({ lang: this.lang });

    },
    methods: {
        async getSimList(): Promise<void> {

            const getSimCardRes = await getSimCardList({
                includes: [ESimCardModule.CLOUD_SERVICES, ESimCardModule.LAST_USED_DEVICE, ESimCardModule.SIM_CARD, ESimCardModule.CURRENT_DATA_USAGE],
                limit: this.pageInfo.limit,
                page: this.pageInfo.page,
                sortStrategy: 'selectSimCard',
                lang: this.$i18n.locale
            });
            const simInfoList = getSimCardRes.items.map( item => new SimManager( item, getSimCardRes.services ))
                .filter( simManager => !simManager.isRetired );
            await SimManager.initSimManagers( simInfoList );
            this.simInfoList = [...this.simInfoList, ...simInfoList];
            this.pageInfo.page++;
            this.pageInfo.total = getSimCardRes.totalRows;

        },
        async infiniteHandler( $state: StateChanger ) {

            const pageNum = Math.ceil(  this.pageInfo.total / this.pageInfo.limit );
            if ( this.pageInfo.page > pageNum ) {

                $state.complete();

            } else {

                try {

                    nprogress.start();
                    await this.getSimList();

                } catch ( e ) {

                    console.error( e );
                    unfoldError( 'errors.server_error' );
                    $state.error();

                } finally {

                    nprogress.done();

                }
                if ( this.simInfoList.length === 0 ) {

                    $state.complete();

                } else {

                    $state.loaded();

                }

            }

        },
        changeSim( simManager: SimManager, selected: boolean ) {

            this.simInfoList = this.simInfoList.map( simInfo => {

                if ( simInfo.iccid === simManager.iccid ) {

                    simInfo.selected = selected;

                } else {

                    simInfo.selected = false;

                }
                return simInfo;

            });

        },
        handleNext() {

            const selectedSimManager = this.simInfoList.find( item => item.selected );
            if ( !selectedSimManager ) {

                console.warn( 'no selected sim card!' );

            }
            this.$emit( 'next', selectedSimManager?.data.simCard?.simCardCode, selectedSimManager?.currentSubscription?.id );

        }
    }
});
</script>

<style lang="less" scoped>
.select-sim-dialog {
    box-sizing: border-box;
    padding: 0 64px;
    width: 1128px;
    display: flex;
    gap: 16px;
    flex-wrap: wrap;
    text-align: left;
    margin-bottom: 16px;

    @media @mobile {
        width: 100%;
        box-sizing: border-box;
        padding: 0 12px;
        gap: 12px;
        margin-bottom: 2px;
    }
    .loading{
        width: 100%;
    }

    .processing-container{
        height: calc(100vh - 142px);
        display: flex;
        align-items: center;
        justify-content: center;
    }
}
</style>
